/**
 * Item type classification helpers.
 * Single source of truth for determining what kind of item an ID represents.
 * Shared between VocabStep, LessonItemsList, SmartReviewPanel, AudioItem.
 */

/** @param {string} id */
export function isVocabItem(id) { return id.startsWith('v') || id.startsWith('l'); }

/** @param {string} id */
export function isSentenceItem(id) { return (id.startsWith('s') && !id.startsWith('s:')) || id === 'title'; }

/**
 * Canonicalize an item ID so the three ID formats compare equal.
 *
 * `submissions.sentenceId` and `class_assignments.included_items` are written by
 * three producers that do not agree on a format:
 *   - lesson items      → positional, `s1` / `v1` / `title`
 *   - Smart Review      → always 's'-prefixed (`normalizeSentenceId`), plus `v:<word>`
 *   - project sentences → 's'-prefixed since 2026-08-18; BARE global index (`"17"`) before that
 * An auto-extra copies `submission.sentenceId` verbatim, so one `included_items`
 * list routinely holds several formats at once — `"17"` and `"s17"` can even be
 * the same sentence arriving by two routes.
 *
 * **Canonicalize at the boundary.** Classify or compare IDs only after passing
 * them through here; never call `isVocabItem` / `isSentenceItem` on a raw ID off
 * a submission or an `included_items` array. Skipping this is what let a project
 * pick match neither predicate and vanish from the UI while the backend still
 * required it — the student saw all-green on an assignment that could never
 * complete (prod bug: assignment 12108).
 *
 * Bare-numeric → 's'-prefixed. Vocab IDs (`v1`, `v:entire`, `l3`) and sentinels
 * (`combined`, `combined-vocab`, `title`) pass through unchanged. Mirrors
 * `canonicalSentenceId` in `backend/utils/itemTypes.js` — keep them identical.
 *
 * @param {string|number} id
 * @returns {string}
 */
export function canonicalSentenceId(id) {
  const s = String(id);
  if (/^\d+$/.test(s)) return `s${s}`;
  return s;
}

/** The one video a video project's child films of the whole page range. Mirrors backend utils/itemTypes.js. */
export const PROJECT_VIDEO_ITEM_ID = 'project-video';

/** @param {string} id */
export function isCombinedItem(id) { return id === 'combined'; }

/** @param {string} id */
export function isCombinedVocabItem(id) { return id === 'combined-vocab'; }

/** The 总结 retelling. A failed one goes into the next-day extra as this item. */
export const SUMMARY_ITEM_ID = 'recap';

/** @param {string} id */
export function isSummaryItem(id) { return id === SUMMARY_ITEM_ID; }

/** A failed AI-tutor question in an extra: 'ai:<questionId>'. Mirrors backend aiTutor.completion.js. */
export const AI_TUTOR_ITEM_PREFIX = 'ai:';

/** @param {string} id */
export function isAiTutorItem(id) { return String(id).startsWith(AI_TUTOR_ITEM_PREFIX); }

/** @param {string} id */
export function isKnownItemType(id) {
  return isVocabItem(id) || isSentenceItem(id) || isCombinedItem(id) || isCombinedVocabItem(id)
    || isSummaryItem(id) || isAiTutorItem(id);
}

/**
 * Canonicalize, dedupe, and partition an `included_items` pick list by kind.
 *
 * The single place a pick list gets split. Every bucket comes from the SAME
 * canonicalized list, and anything matching no known kind lands in
 * `unclassified` rather than vanishing between two `.filter()` calls — the
 * silent drop described on canonicalSentenceId above. Treat a non-empty
 * `unclassified` as a bug: a new ID format has reached the UI unhandled.
 *
 * @param {string[]|null|undefined} includedItems
 */
export function partitionIncludedItems(includedItems) {
  const canonical = [...new Set((includedItems || []).map(canonicalSentenceId))];
  return {
    vocab: canonical.filter(isVocabItem),
    sentences: canonical.filter(isSentenceItem),
    combined: canonical.filter(isCombinedItem),
    combinedVocab: canonical.filter(isCombinedVocabItem),
    summary: canonical.filter(isSummaryItem),
    aiTutor: canonical.filter(isAiTutorItem).map((id) => id.slice(AI_TUTOR_ITEM_PREFIX.length)),
    unclassified: canonical.filter((id) => !isKnownItemType(id)),
  };
}

/**
 * True when an included_items list carries the 'combined' passage marker (the
 * whole-lesson recitation item). Routes through isCombinedItem so the 'combined'
 * literal lives in exactly one place. Safe on null/non-array input.
 * @param {unknown} items
 */
export function includesCombinedMarker(items) {
  return Array.isArray(items) && items.some(isCombinedItem);
}

/**
 * True when an included_items list carries the 'combined' marker ALONGSIDE at
 * least one non-combined pick (a specific vocab/sentence item). Such Smart-Review
 * extras render their own 词汇/句子/整课读 tab row, so the sentences tab must NOT
 * be force-selected — doing so hides the other picks and leaves the assignment
 * uncompletable (prod bug 8841).
 *
 * A combined-ONLY extra (e.g. ["combined"]) is NOT "mixed": it behaves like a
 * plain combined review and DOES want the sentences tab forced, otherwise it
 * falls back to the vocab tab and plays the main video instead of the sentences
 * video (mkk b3 L16 combined auto-extra bug).
 * @param {unknown} items
 */
export function isMixedCombinedExtra(items) {
  return (
    Array.isArray(items) &&
    items.some(isCombinedItem) &&
    items.some((id) => !isCombinedItem(id))
  );
}

/**
 * True when the active tab should play the sentences video rather than the
 * main/vocab video. Video choice must follow the tab's identity, not its numeric
 * index: VocabStep's dynamic-tabs branch builds its tab row from included_items
 * in vocab → sentences → combined → combined-vocab order, so a combined-marker
 * Smart-Review extra with NO vocab pick puts 句子 at index 0 — where every other
 * layout has 词汇 (prod bug: assignment 10730, included_items ["s4","combined"]
 * played the main video on the index-0 句子 tab).
 * @param {{ combinedMode: boolean, includedItems: unknown, activeTab: number }} params
 */
/**
 * Is the AI-tutor tab actually drawn on this assignment?
 *
 * Having tutor content is not enough. VocabStep draws the tab on the plain
 * lesson tab row only: a combined 通读 assignment hides that row outright
 * (`willShowTabs = showTabs && !combinedMode`), and a smart-review extra builds
 * its own row out of the specific items a teacher picked. Every caller that
 * branches on "index 2 is the tutor" has to apply the same rule, or a mixed
 * extra like ["v6","v:grew","combined"] — which deliberately stays on tab 0 —
 * loses the main video it is supposed to show, and a three-tab dynamic extra
 * gets the main video on its 整课读 tab instead of the sentence breakdown.
 *
 * Same rule as showsTutorTab() in backend aiTutor.completion.js, so what an
 * assignment REQUIRES and what it DRAWS cannot drift apart.
 */
export function isAiTutorTabDrawn({ hasTutorContent, combinedMode, includedItems }) {
  return !!hasTutorContent && !combinedMode && !includedItems?.length;
}

/**
 * Whether the 总结 tab is drawn: the tutor's tab-row shape, and only the first
 * time the lesson is set — a review pass (reviewIndex > 0) does not ask the
 * child to retell the story again. Same rule as summaryRequirementFor() in
 * backend aiTutor.completion.js.
 */
export function isSummaryTabDrawn({ hasSummaryContent, combinedMode, includedItems, reviewIndex }) {
  return isAiTutorTabDrawn({ hasTutorContent: hasSummaryContent, combinedMode, includedItems })
    && !((reviewIndex ?? 0) > 0);
}

export function isSentencesVideoTab({ combinedMode, includedItems, activeTab, aiTutorTab = false }) {
  // Dynamic-tabs layout: index 0 is the main video only when a vocab pick claims it.
  if (combinedMode && includesCombinedMarker(includedItems)) {
    return !includedItems.some(isVocabItem) || activeTab !== 0;
  }
  // Index 2 is 整课读 on a normal tabbed lesson — but it is the AI-tutor tab on a
  // lesson that has one, and the tutor shows no video at all. Without this,
  // opening the tutor tab on a Monkey King lesson (which has main + main2 +
  // sentences videos) makes DualVideoRenderer hide the story cartoon and show
  // the sentence-breakdown video instead, for no reason the child can see.
  if (aiTutorTab && activeTab === 2) return false;
  // Fixed layout: 0 = 词汇 (main), 1 = 句子 and 2 = 整课读 (both sentences).
  return activeTab === 1 || activeTab === 2;
}

/** Ensure an item ID has the 'v' prefix (e.g. '1' → 'v1', 'v1' → 'v1') */
export function normalizeVocabId(id) {
  return id.startsWith('v') ? id : `v${id}`;
}

/**
 * Ensure an item ID has the 's' prefix, except 'title' which stays as-is.
 * Write-side only — it happily produces 'sv3' from 'v3'. To compare or classify
 * an ID of unknown kind, use canonicalSentenceId, which touches only bare digits.
 */
export function normalizeSentenceId(id) {
  if (id === 'title') return id;
  return id.startsWith('s') ? id : `s${id}`;
}
